/**
 * Constants for controlling MessageInput component sections visibility
 */

export const MessageInputControl = {
  /** The line under the box: the permission menu and the agent chip ("where this chat runs") */
  AGENT_LINE: "agent_line",
  /** File upload functionality */
  FILE_UPLOAD: "file_upload",
  /** Entity selector for connections and references */
  ENTITY_SELECTOR: "entity_selector",
  /** Skills button (/) */
  SKILLS: "skills",
  /** Suggested actions menu button */
  SUGGESTED_ACTIONS: "suggested_actions",
  /** Placeholder insertion button ({}) */
  PLACEHOLDERS: "placeholders",
  /** Audio recording/microphone button */
  MICROPHONE: "microphone",
  /** Send button */
  SEND_BUTTON: "send_button",
  /** The right-hand dial: Cohesive's effort tier, or a subscription's model + thinking */
  REASONING_EFFORT: "reasoning_effort",
} as const;

export const MessageInputControlSet = {
  /** Default visible sections for MessageInput */
  DEFAULT: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.SUGGESTED_ACTIONS,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
  ],
  /** Home / workspace hero composer — the default controls plus the effort dial and the line
   *  under the box. Session-less until send, so the agent and permission mode are drafts: the
   *  agent rides the create, the permission mode the first message. */
  HOME_COMPOSER: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.SUGGESTED_ACTIONS,
    MessageInputControl.REASONING_EFFORT,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
    MessageInputControl.AGENT_LINE,
  ],
  /** The new home's composer. Send starts a canvas (or, below lg, a chat), so only the
   *  controls that act before a session exists: file upload (into the private workspace, the
   *  ids ride the first message), add context, Skills, the effort dial, mic, send, and the line. */
  NEW_HOME: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.REASONING_EFFORT,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
    MessageInputControl.AGENT_LINE,
  ],
  /** First run: the new home's toolbar with no line — it stays on Cohesive with the default
   *  permissions. */
  FIRST_RUN: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.REASONING_EFFORT,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
  ],
  /** Session side pane — the default chat controls, the effort dial and the line (the chip
   *  locked: the pane only ever holds a started chat). */
  SIDECHAT: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.SUGGESTED_ACTIONS,
    MessageInputControl.REASONING_EFFORT,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
    MessageInputControl.AGENT_LINE,
  ],
  /** Canvas session pane — file upload (lands as `class=canvas` and on the canvas as a node),
   *  add context (connections), skills, the effort dial, mic, send, and the line. */
  CANVAS_CHAT: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.REASONING_EFFORT,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
    MessageInputControl.AGENT_LINE,
  ],
  /** Full-page workspace chat — same controls as CANVAS_CHAT. */
  WORKSPACE_CHAT: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.REASONING_EFFORT,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
    MessageInputControl.AGENT_LINE,
  ],
  /** Floating canvas quick chat and the canvas chat node — same controls as CANVAS_CHAT. */
  CANVAS_QUICK_CHAT: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.REASONING_EFFORT,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
    MessageInputControl.AGENT_LINE,
  ],
  /** For editing suggested actions - minimal with placeholders only */
  SUGGESTED_ACTIONS_EDITOR: [
    MessageInputControl.SKILLS,
    MessageInputControl.PLACEHOLDERS,
    MessageInputControl.SEND_BUTTON,
  ],
  FULL: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.SUGGESTED_ACTIONS,
    MessageInputControl.PLACEHOLDERS,
    MessageInputControl.REASONING_EFFORT,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
    MessageInputControl.AGENT_LINE,
  ],
  SEND_ONLY: [],
} as const;

export type MessageInputControl =
  (typeof MessageInputControl)[keyof typeof MessageInputControl];

export type MessageInputControlSet = readonly MessageInputControl[];

/**
 * Utility functions for working with MessageInput sections
 */
export const MessageInputControlUtils = {
  /**
   * Check if a specific section is visible
   */
  isVisible: (
    sections: MessageInputControlSet,
    section: MessageInputControl,
  ): boolean => sections.includes(section),
};

/**
 * Usage Examples:
 *
 * ```tsx
 * import { MessageInputControl, MessageInputControlSet } from "@/constants/message-input";
 *
 * // Default configuration (file upload, entity selector, skills, suggestions, microphone)
 * <MessageInput controlSet={MessageInputControlSet.DEFAULT} />
 *
 * // Full-featured input (all controls)
 * <MessageInput controlSet={MessageInputControlSet.FULL} />
 *
 * // Custom: Agent selector + entity selector + microphone
 * <MessageInput
 *   controlSet={[
 *     MessageInputControl.AGENT_LINE,
 *     MessageInputControl.ENTITY_SELECTOR,
 *     MessageInputControl.MICROPHONE
 *   ]}
 * />
 *
 * // Completely hidden toolbar (just text input and send button)
 * <MessageInput controlSet={[]} />
 *
 * // Agent-focused input
 * <MessageInput
 *   controlSet={[
 *     MessageInputControl.AGENT_LINE,
 *     MessageInputControl.SKILLS,
 *     MessageInputControl.MICROPHONE
 *   ]}
 * />
 * ```
 */
